import React , { useState } from 'react';
import { ProductCard, DynamicTypography } from '../../components';
import { CardType } from 'models/type/ProductCardType';
import { RoomUpgrade } from 'models/RoomUpgrade';
import { Food } from 'models/Food';
import { Facilities } from 'models/Facilities';
import Fab from '@material-ui/core/Fab';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { getVerbiage } from 'utils/stringHelper';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/state';

interface IProps  {
  data: any[];
  onSetMessage: (message: string) => void;
  onCompleted: (isCompleted: boolean) => void;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
        position: 'fixed',
        bottom: theme.spacing(2), 
        right: theme.spacing(2), 
        zIndex: theme.zIndex.appBar + 1, 
        padding: theme.spacing(1),
        borderRadius: theme.shape.borderRadius,
        cursor: 'default', 
    },
  }),
);

function isRoomUpgrade(data: any[]) : data is RoomUpgrade[] {
    return data.every(item => 
        'id' in item &&
        'name' in item &&
        'details' in item &&
        'price' in item &&
        'image' in item &&
        !('quantity' in item) && 
        !('hourBooking' in item) 
    );
};

function isRoomService(data: any[]): data is Food[] {
    return data.every(item => 
        'id' in item &&
        'name' in item &&
        'details' in item &&
        'price' in item &&
        'image' in item &&
        'quantity' in item && 
        item.price != 0 &&
        item.details != ""
    );
};

function isFacility(data: any[]): data is Facilities[] {
    return data.every(item => 
        'hourBooking' in item
    );
};

function isAmenity(data: any[]): data is Food[] {
    return data.every(item => 
        'id' in item &&
        'name' in item &&
        'details' in item &&
        'price' in item &&
        'image' in item &&
        'quantity' in item &&
        item.price === 0 &&
        item.details === ""
    );
};

export const ListingItemCard: React.FC<IProps> = ({ data, onSetMessage, onCompleted}) => {
    const [selectedRoom, setSelectedRoom] = useState<RoomUpgrade | null>(null);
    const [totalPrices, setTotalPrices] = useState<{ [key: number]: number }>({});
    const [totalCount, setTotalCount] = useState<{ [key: number]: number }>({});
    const [selectedFacilities, setSelectedFacilities] = React.useState<CardType[]>([]);
    const totalPrice = selectedFacilities.reduce((total, facility) => total + facility.price, 0);
    const { verbiages } = useSelector((state: RootState) => state.verbiages);
    const classes = useStyles();
    const handleRoomSelect = (room: CardType | null) => {
        if (selectedRoom && selectedRoom.id === room?.id) {
            setSelectedRoom(null);
            onSetMessage("");
            onCompleted(false);
        } else {
            setSelectedRoom(room);
            onSetMessage(room?.name || "");
            onCompleted(true);
        }
    };

    const handleTotalPriceChanged = (currentTotalPrice: number, cardId: number) =>{
        setTotalPrices((prevTotalPrices) => ({
            ...prevTotalPrices,
            [cardId]: currentTotalPrice,
            }));      
    };

    const handleTotalCountChanged = (currentTotalCount: number, cardId: number) =>{
        setTotalCount((prevCount) => ({
            ...prevCount,
            [cardId]: currentTotalCount,
            }));      
    };

    const handleFacilitiesSelect = (selectedFacility: CardType) => {
        const isSelected = selectedFacilities.some((facility) => facility.id === selectedFacility.id);
        if (isSelected) {
            setSelectedFacilities(selectedFacilities.filter((facility) => facility.id !== selectedFacility.id));
        } else {
            setSelectedFacilities([...selectedFacilities, selectedFacility]);
        }   
    };

    React.useEffect(()=>{
        const totalPriceSum = Object.values(totalPrices).reduce((sum, price) => sum + price, 0);
        onCompleted(totalPriceSum > 0);
        if(totalPriceSum>0){
            onSetMessage("Room Service");
        }
        else{
            onSetMessage("");
        }
    }, [totalPrices]);

    React.useEffect(()=>{
        const totalCountSum = Object.values(totalCount).reduce((sum, count) => sum + count, 0);
        onCompleted(totalCountSum > 0);
        if(totalCountSum>0){
            onSetMessage("Amenities");
        }
        else{
            onSetMessage("");
        }
    }, [totalCount]);
    
    React.useEffect(() => {
        onCompleted(selectedRoom !== null);
    }, [selectedRoom]);

    React.useEffect(() => {
        onCompleted(selectedFacilities.length > 0);
        onSetMessage("Facility");
    }, [selectedFacilities]);
    
    return (  
        isRoomUpgrade(data) ? (
            <div>
                <DynamicTypography
                    color="textPrimary"
                    align="center"
                    variant="h6"
                    style={{fontWeight: 'bold'}}
                    content={`${getVerbiage(
                        verbiages,
                        'weblbl096'
                    )}`}
                ></DynamicTypography>
                {data.map((room) => (
                    <ProductCard
                        key={room.id}
                        productCard={room}
                        onSelectCard={handleRoomSelect}
                        selectedCard={selectedRoom !== null && selectedRoom.id === room.id}
                        counterButtonOn={false}
                    />
                ))}
                {selectedRoom ? <Fab className={classes.root} variant='extended'>{`Total Price: SGD ${selectedRoom.price.toFixed(2)}`}</Fab> : null}
            </div>
        ): isRoomService(data) ? (
            
            <div>
                <DynamicTypography
                    color="textPrimary"
                    align="center"
                    variant="h6"
                    style={{fontWeight: 'bold'}}
                    content={`${getVerbiage(
                        verbiages,
                        'weblbl097'
                    )}`}
                ></DynamicTypography>
                {data.map((item) => (
                    <ProductCard
                        key={item.id}
                        productCard={item}
                        counterButtonOn={true}
                        onTotalPriceChange={(currentTotalPrice) => handleTotalPriceChanged(currentTotalPrice, item.id)}
                    />
                ))}
            
                <Fab className={classes.root} variant='extended'>{`Total Price: SGD ${Object.values(totalPrices).reduce((sum, price) => sum + price, 0).toFixed(2)}`}</Fab>                
            </div>               
            
        ):
        isFacility(data) ? (            
            <div>
                <DynamicTypography
                    color="textPrimary"
                    align="center"
                    variant="h6"
                    style={{fontWeight: 'bold'}}
                    content={`${getVerbiage(
                        verbiages,
                        'weblbl098'
                    )}`}
                ></DynamicTypography>
                {data.map((facilities) => (
                    <ProductCard
                    key={facilities.id}
                    productCard={facilities}
                    onSelectCard={handleFacilitiesSelect}
                    selectedCard={selectedFacilities.some((facility) => facility.id === facilities.id)}
                    counterButtonOn={false}
                    />
                ))}
                {selectedFacilities.length > 0 && <Fab className={classes.root} variant='extended'>{`Total Price: SGD ${totalPrice.toFixed(2)}`}</Fab>}        
            </div>            
        ):
        isAmenity(data) ? (
            
            <div>
                <DynamicTypography
                    color="textPrimary"
                    align="center"
                    variant="h6"
                    style={{fontWeight: 'bold'}}
                    content={`${getVerbiage(
                        verbiages,
                        'weblbl099'
                    )}`}
                ></DynamicTypography>
                    {data.map((item) => (
                        <ProductCard
                            key={item.id}
                            productCard={item}
                            counterButtonOn={true}
                            onTotalCountChange={(currentCount) => handleTotalCountChanged(currentCount, item.id)}
                        />
                    ))}
            </div>
            
        ):
        null   
    );
};
